import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Label } from '@components/Label';
import { PDFPreviewModal } from '@pages/customer/common/file';
import { AttachmentFile } from '@pages/customer/common/file/AttatchmentFile';
import { useStore } from '@stores/RootStore';
import type { PdfFilePreview } from '@customHooks/useFileDialog';
import { useSubscriptionModel } from '@customHooks/useSubscriptionModel';
import { useDownloadFile } from '@queryHooks/useFile';
import { download } from '@utils/fileUtil';
import * as S from './HistoryStyles';

export const History = observer(() => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();

  const { categoryStore } = useStore();
  const subscription = useSubscriptionModel();
  const { subscriptionRequest } = subscription;
  const { mutateAsync: downloadFile } = useDownloadFile();
  const category = subscription.softwareCategory
    ? categoryStore.findRootCategory(subscription.softwareCategory.id)
    : null;
  const categoryName = category?.name || subscription.softwareCategoryName;

  const histories = subscriptionRequest.getHistories(true);

  const [pdfPreview, setPdfPreview] = useState<PdfFilePreview>({
    open: false,
    file: null,
    fileName: '',
    fileId: null,
    numPages: 0,
  });

  const findAttachment = (fileId: string) => {
    const historyAttachments = histories.flatMap(history => history.attachments);
    return historyAttachments.find(file => file?.fileId === fileId);
  };

  const handlePdfPreview = async (fileId: string) => {
    const attachment = findAttachment(fileId);

    if (!attachment) {
      toastStore.open({ message: t('File.Not_Found') });
      return;
    }

    if (attachment.fileExtension?.toLowerCase() !== 'pdf') {
      toastStore.open({ message: t('File.PDF_Only') });
      return;
    }

    try {
      const fileBlob = await downloadFile(fileId);
      setPdfPreview({
        open: true,
        file: fileBlob,
        fileName: `${attachment.fileName}.${attachment.fileExtension}`,
        fileId,
        numPages: 0,
      });
    } catch (error) {
      console.error(t('File.Load_Failed'), error);
      toastStore.open({ message: t('File.Load_Failed') });
    }
  };

  // 파일 다운로드
  const handleDownloadFile = async (fileId: string) => {
    try {
      const historyAttachments = histories.flatMap(history => history.attachments);
      const attachment = historyAttachments.find(file => file?.fileId === fileId);
      if (!attachment) {
        toastStore.open({ message: t('File.Not_Found') });
        return;
      }
      const fileBlob = await downloadFile(fileId);
      const fileFullName = `${attachment.fileName}.${attachment.fileExtension}`;
      download(fileBlob, fileFullName);
    } catch (error) {
      console.error(t('File.Download_Failed'), error);
      toastStore.open({ message: t('File.Download_Failed') });
    }
  };

  // PDF 미리보기 변경
  const handleChangePdfPreview = (pdfPreview: PdfFilePreview) => {
    setPdfPreview(pdfPreview);
  };

  return (
    <S.Container>
      <S.HistoryList>
        <S.HistoryItem>
          <Typography variant="headline7" component="strong">
            {t('Subscrib_GNB_06')}
          </Typography>
          <S.SubBodyWrap>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_UseRequest_06')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {categoryName}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_UseRequest_25')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscription.tenant.name}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_UseRequest_07')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscription.software.name}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_BuyRequest_06')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscriptionRequest.formattedCreatedDate}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Main_12')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscription.plan.planName}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_BuyRequest_02')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscriptionRequest.user.email || '-'}
              </Typography>
            </S.SubItem>
            <S.SubItem>
              <S.SubItemTitle variant="headline9" component="strong">
                {t('Subscrib_Detail_UseRequest_15')}
              </S.SubItemTitle>
              <Typography variant="headline8" component="p">
                {subscriptionRequest.inquiry || '-'}
              </Typography>
            </S.SubItem>
          </S.SubBodyWrap>
        </S.HistoryItem>
        <S.HistoryItem>
          <Typography variant="headline7" component="strong">
            {t('Request_Processing')}
          </Typography>
          {subscriptionRequest
            .getHistories(true)
            .slice(0)
            .reverse()
            .map(history => {
              return (
                <S.SubBodyWrap key={history.createdDate}>
                  <S.SubItem>
                    <S.SubItemTitle variant="headline9" component="strong">
                      {t('Subscrib_Detail_UseRequest_09')}
                    </S.SubItemTitle>
                    <Typography variant="headline8" component="div">
                      {subscription.subscriptionRequest.isValidModel ? (
                        <Label
                          variant="contain"
                          color={subscription.subscriptionRequest.getStatusLabelColor(history.status)}
                        >
                          {subscription.subscriptionRequest.getStatusStr(history.status)}
                        </Label>
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </S.SubItem>

                  <S.SubItem>
                    <S.SubItemTitle variant="headline9" component="strong">
                      {t('Subscrib_Detail_UseRequest_19')}
                    </S.SubItemTitle>
                    <Typography variant="headline8" component="p">
                      {history.createdDate}
                    </Typography>
                  </S.SubItem>

                  <S.SubItem>
                    <S.SubItemTitle variant="headline9" component="strong">
                      {t('Acc_Detail_12')}
                    </S.SubItemTitle>
                    <Typography variant="headline8" component="p">
                      {history.managerName}
                    </Typography>
                  </S.SubItem>
                  <S.SubItem>
                    <S.SubItemTitle variant="headline9" component="strong">
                      {t('Subscrib_Detail_UseRequest_15')}
                    </S.SubItemTitle>
                    <Typography variant="headline8" component="p">
                      {history.comment}
                    </Typography>
                  </S.SubItem>
                  <S.SubItem>
                    <S.SubItemTitle variant="headline9" component="strong">
                      {t('Subscrib_Detail_UseRequest_40')}
                    </S.SubItemTitle>
                    <Typography variant="headline8" component="p">
                      <S.AttachmentSection>
                        {history.attachments.map(attachment => (
                          <AttachmentFile
                            key={attachment.fileId}
                            attachment={{
                              fileId: attachment.fileId,
                              fileName: attachment.fileName,
                              fileExtension: attachment.fileExtension,
                              file: new File([], `${attachment.fileName}.${attachment.fileExtension}`),
                            }}
                            showPdfPreviewFn={handlePdfPreview}
                            downloadFileFn={handleDownloadFile}
                            enabledDeleteFile={false}
                            enabledDownload
                          />
                        ))}
                      </S.AttachmentSection>
                    </Typography>
                  </S.SubItem>
                </S.SubBodyWrap>
              );
            })}
        </S.HistoryItem>
      </S.HistoryList>
      {pdfPreview.open && (
        <PDFPreviewModal
          pdfPreview={pdfPreview}
          updatePdfPreview={handleChangePdfPreview}
          downloadFileFn={handleDownloadFile}
          enabledDownload
        />
      )}
    </S.Container>
  );
});
